import React, { useState } from 'react';
import { Typography } from 'antd';
import heroImage from '../assets/Designer_basic.png';
import { getThemeStyles } from '../theme';
import HeroImageAnimation from '../components/HeroImageAnimation';

const { Paragraph } = Typography;

const splashDark = getThemeStyles('dark');

const Splash = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [cubeOffset, setCubeOffset] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePos({ x, y });

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2 - 14;
    const mouseX = e.clientX;
    const mouseY = e.clientY;
    const distance = Math.sqrt(Math.pow(mouseX - centerX, 2) + Math.pow(mouseY - centerY, 2));
    
    if (distance < 150) {
      const angle = Math.atan2(centerY - mouseY, centerX - mouseX);
      const strength = (150 - distance) / 150;
      setCubeOffset({ x: Math.cos(angle) * 5 * strength, y: Math.sin(angle) * 5 * strength });
    } else {
      setCubeOffset({ x: 0, y: 0 });
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Hero Section */}
      <div style={{ 
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: '60px 24px', 
        background:
          'radial-gradient(ellipse 85% 60% at 50% 42%, rgba(21, 60, 180, 0.16) 0%, transparent 55%), radial-gradient(ellipse 55% 45% at 50% 58%, rgba(148, 22, 127, 0.1) 0%, transparent 50%), linear-gradient(180deg, #06030f 0%, #0f1528 22%, #12102a 100%)',
        borderBottom: '1px solid rgba(148, 22, 127, 0.3)',
        overflow: 'hidden'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '40px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                margin: '4px 4px 0 4px',
                marginBottom: 28,
                fontSize: '52px',
                lineHeight: 1.12,
                fontWeight: 1000,
                textShadow: '0 0 10px rgba(255, 255, 255, 0.12), 0 0 20px rgba(255, 255, 255, 0.08)',
                backgroundImage: 'linear-gradient(135deg, #F62E97 0%, #E93479 26%, #94167F 48%, #153CB4 68%, #1a4f9e 88%, #153CB4 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                backgroundSize: '100% 100%',
              }}
            >
              Enterprise AI Platform - Low Code RAG Builder
            </div>
            <Paragraph style={{ 
              fontSize: '18px', 
              color: splashDark.textPrimary, 
              lineHeight: '1.65',
              margin: 0,
              marginTop: 4,
              maxWidth: 720,
            }}>
            A modular, scalable framework that simplifies and accelerates the development of Retrieval-Augmented Generation solutions by offering intelligent document chunking, vector integration, and search capabilities - all without writing a single line of code!
            </Paragraph>
          </div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }} onMouseMove={handleMouseMove}>
            <img 
              src={heroImage} 
              alt="RAG Platform" 
              onLoad={() => setImageLoaded(true)}
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                maskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 75%)',
                WebkitMaskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 75%)',
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 1s ease-in',
                filter: 'brightness(0.8) contrast(1.2)',
                animation: imageLoaded ? 'fadeIn 1s ease-in' : 'none'
              }} 
            />
            <div style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0,
              background: 'radial-gradient(circle at center, rgba(246, 46, 151, 0.3) 0%, transparent 50%)',
              animation: 'pulse 10s ease-in-out infinite',
              pointerEvents: 'none'
            }} />
            <div style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0,
              backdropFilter: `blur(${Math.abs(Math.sin(mousePos.x / 10)) * 3}px)`,
              WebkitBackdropFilter: `blur(${Math.abs(Math.sin(mousePos.x / 10)) * 3}px)`,
              maskImage: `radial-gradient(circle 120px at ${mousePos.x}% ${mousePos.y}%, black 0%, transparent 100%)`,
              WebkitMaskImage: `radial-gradient(circle 120px at ${mousePos.x}% ${mousePos.y}%, black 0%, transparent 100%)`,
              pointerEvents: 'none',
              transition: 'all 0.1s ease-out'
            }} />
            <div style={{ 
              position: 'absolute', 
              top: 'calc(50% + 40px)', 
              left: '50%', 
              width: '200px',
              height: '100px',
              transform: 'translate(-50%, -50%)',
              background: 'radial-gradient(ellipse at center, rgba(246, 46, 151, 0.5) 0%, rgba(21, 60, 180, 0.3) 40%, transparent 70%)',
              filter: 'blur(10px)',
              animation: 'wobble 10s ease-in-out infinite',
              pointerEvents: 'none'
            }} />
            <div style={{ 
              position: 'absolute', 
              top: 'calc(50% + 40px)', 
              left: '50%', 
              width: '180px',
              height: '90px',
              transform: 'translate(-50%, -50%)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)',
              animation: 'wobble 10s ease-in-out infinite',
              pointerEvents: 'none'
            }} />
            <div style={{ position: 'absolute', top: 'calc(50% - 14px)', left: '50%', transform: `translate(calc(-50% + ${cubeOffset.x}px), calc(-50% + ${cubeOffset.y}px))`, animation: 'fadeIn 1s ease-in', transition: 'transform 0.2s ease-out' }}>
              <HeroImageAnimation />
            </div>
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes pulse {
                0%, 100% { opacity: 0.5; transform: scale(1); }
                50% { opacity: 0.8; transform: scale(1.15); }
              }
              @keyframes wobble {
                0%, 100% { transform: translate(-50%, -50%) scaleX(1); }
                25% { transform: translate(-50%, -50%) scaleX(1.1) scaleY(0.95); }
                50% { transform: translate(-50%, -50%) scaleX(0.95) scaleY(1.05); }
                75% { transform: translate(-50%, -50%) scaleX(1.05) scaleY(0.98); }
              }
            `}</style>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Splash;
